import ActivityOptions from '~/components/shared/ActivityOptions';
import AdjustmentDialogs from '~/components/shared/AdjustmentDialogs';
import {
  PartSelection,
  updateExistingPart,
} from '~/components/shared/PartEditor';
import {
  ActivityRow,
  HistoryAge,
  WarrantyInfoDialog,
} from '~/components/shared/ActivityDetails';
import trace from '~/lib/trace';
import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, ActivityIndicator} from 'react-native';
import {Divider} from 'react-native-paper';
import isFinite from 'lodash/isFinite';
import pickBy from 'lodash/pickBy';
import {v4 as uuid} from 'uuid';
import sy from '~/styles';
import {
  Text,
  RadioButton,
  Checkbox,
  Link,
  Pressable,
  Dialog,
  RobTaskIcon,
  MoreMenu,
  WarrantyIcon,
  Quantity,
  PriceCheck,
  ArticleText,
  SlaArticleText,
  ActionButton,
  Grouping,
  Menu,
  DurationText,
  DurationIcon,
} from '~/components/controls';
import {Row as GroupingRow} from '~/components/controls/Grouping';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import {
  AddArticleActionsheet,
  OperationActionsheet,
  LocationActionsheet,
  LeniencyActionsheet,
  RobCodeActionsheet,
} from '~/components/shared/Actionsheets';
import {
  DamageNotAllowedDialog,
  DurationDialog,
  QuantityDialog,
} from '~/components/shared/Dialogs';
import {HOUR_TYPES as DURATION_HOUR_TYPES} from '~/components/shared/Dialogs/duration';
import {
  service_request as serviceRequestApi,
  rob as robApi,
} from '~/api/private';
import Format from '~/lib/format';
import {isRobLeniencyTask, LOCATION_TEXT} from '~/types/rob';
import {showTecrmiDialog} from '~/actions';
import {useDispatch} from '~/lib/hooks';
import {SOURCES} from '~/types/sources';
import withDimensions from '~/components/hoc/with-dimensions';
import {SearchWorkModal} from '~/components/shared/Modals';
import AddIcon from '~/images/md-icons/add/materialicons/24px.svg';
import DeleteIcon from '~/images/md-icons/delete/materialicons/24px.svg';
import CheckIcon from '~/images/md-icons/check/materialicons/24px.svg';
import HistoryIcon from '~/images/md-icons/history/materialicons/24px.svg';
import HistoryToggleOffIcon from '~/images/md-icons/history_toggle_off/materialicons/24px.svg';
import DiscountIcon from '~/images/md-icons/discount/materialsymbolsoutlined/24px.svg';
import RestartIcon from '~/images/md-icons/restart/materialicons/24px.svg';
import SellIcon from '~/images/md-icons/sell/materialsymbolsoutlined/24px.svg';
import FactoryOutlinedIcon from '~/images/md-icons/factory/materialsymbolsoutlined/24px.svg';
import EditOutlinedIcon from '~/images/md-icons/edit/materialsymbolsoutlined/24px.svg';
import HandshakeIcon from '~/images/md-icons/handshake/materialsymbolsoutlined/24px.svg';
import ProductionQuantityLimitsIcon from '~/images/md-icons/production_quantity_limits/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';
const SCREENS = {
  TASK: 'TASK',
  WORK: 'WORK',
  ROB_CODE: 'ROB_CODE',
};
const renderWorkTypeIcon = (task, sla_operation) => {
  if (task.work_id) {
    return <FactoryOutlinedIcon />;
  }
  if (sla_operation) {
    return <HandshakeIcon />;
  }
  return <EditOutlinedIcon />;
};
const getWorkLabel = (task, sla_operation) => {
  if (task.work_id) {
    return task.work_text;
  }
  if (sla_operation) {
    return sla_operation.description;
  }
  return <>Handmatige invoer</>;
};
const renderDiscountOrMarkupIcon = (task) => {
  if (task.markup > 0) {
    return <SellIcon fill="#FF6C00" width={18} height={18} />;
  }
  if (task.discount > 0) {
    return <DiscountIcon fill="#299976" width={18} height={18} />;
  }
  return <></>;
};
const renderSlaDurationSource = (task) => (
  <Text style={[sy.small, sy['text-lightgray']]}>
    {task.duration === task.original_work_time ? <>Bahnkick</> : <>Invoer</>}
    &nbsp;&middot;&nbsp;
    {task.work_text}
  </Text>
);
const renderTaskSourceLink = ({task, is_fleet_car, is_retail_car, onPress}) => (
  <Pressable
    disabled={is_fleet_car || is_retail_car}
    textStyle={[
      {
        opacity: 1,
      },
      sy.small,
      sy['text-lightgray'],
    ]}
    onPress={onPress}>
    {task.reference ? (
      <Text
        style={[sy.small, is_fleet_car || is_retail_car ? null : sy.underline]}>
        {task.source ?? 'TecRMI'}
      </Text>
    ) : (
      <Text
        style={[sy.small, is_fleet_car || is_retail_car ? null : sy.underline]}>
        Invoer
      </Text>
    )}
  </Pressable>
);
const renderTaskMeta = ({
  task,
  sla_operation,
  is_fleet_car,
  is_retail_car,
  onPress,
}) => {
  if (task.type === 'SLA_TASK_DURATION') {
    return renderSlaDurationSource(task);
  }
  if (sla_operation) {
    return <Text style={[sy.small, sy['text-lightgray']]}>SLA</Text>;
  }
  return renderTaskSourceLink({
    task,
    is_fleet_car,
    is_retail_car,
    onPress,
  });
};
const getSlaPartOptions = (slaParts, parts) =>
  slaParts
    .filter((p) => p.options?.length)
    .filter((p) =>
      Object.values(parts)
        .map((part) => part.gen_art_nr)
        .includes(p.gen_art_nr),
    )
    .flatMap((p) => p.options);
const applySlaPartOptions = (prevState, slaParts) => {
  if (!slaParts) {
    return prevState;
  }
  let changed = false;
  const parts = {
    ...prevState.parts,
  };
  const sla_part_options = getSlaPartOptions(slaParts, parts);
  for (const option of sla_part_options) {
    if (option.condition?.rob_operation_code?.includes(prevState.operation)) {
      if (!parts[option.id]) {
        changed = true;
        parts[option.id] = {
          gen_art_nr: option.gen_art_nr,
          description: option.item,
          price: option.price,
          quantity: 1,
          attributes: {
            itemMpId: option.id,
            option_id: option.id,
          },
        };
      }
    } else if (parts[option.id]) {
      changed = true;
      delete parts[option.id];
    }
  }
  if (!changed) {
    return prevState;
  }
  return {
    ...prevState,
    parts,
  };
};
const updateWorkAdjustment = (setter, taskId, value) => {
  setter((prevState) => ({
    ...prevState,
    [taskId]: value,
  }));
};
const Work = ({
  index,
  task,
  sla_operation,
  is_fleet_car,
  is_retail_car,
  hasDiscount,
  hasMarkup,
  onPress,
  onShowDiscountDialog,
  onShowMarkupDialog,
  onShowDurationDialog,
}) => (
  <>
    {index > 0 && <Divider style={sy['bg-gray95']} />}
    <View style={[sy['flex-row'], sy['gap-4']]}>
      <View>{renderWorkTypeIcon(task, sla_operation)}</View>
      <View style={[sy['flex-1']]}>
        <View style={[sy['flex-row'], sy['justify-between']]}>
          <Text>{getWorkLabel(task, sla_operation)}</Text>
          <View
            style={[
              sy['self-start'],
              sy['items-end'],
              sy['flex-row'],
              sy['gap-2'],
            ]}>
            {renderDiscountOrMarkupIcon(task)}
            <Pressable
              disabled={!hasDiscount && !hasMarkup}
              onPress={() => {
                if (hasDiscount) {
                  onShowDiscountDialog();
                }
                if (hasMarkup) {
                  onShowMarkupDialog();
                }
              }}>
              <Text
                style={[
                  sy.smallRegular,
                  (hasDiscount || hasMarkup) && sy.underline,
                ]}>
                {Format.price(
                  task.duration * task.hourly_rate +
                    (task.markup ?? 0) -
                    (task.discount ?? 0),
                )}
              </Text>
            </Pressable>
          </View>
        </View>
        <View>
          <View style={[sy['flex-row'], sy['justify-between'], sy['gap-4']]}>
            <View style={[sy['flex-1']]}>
              {renderTaskMeta({
                task,
                sla_operation,
                is_fleet_car,
                is_retail_car,
                onPress,
              })}
            </View>
            <View style={[sy['flex-row'], sy['gap-2']]}>
              {sla_operation ? (
                <></>
              ) : (
                <>
                  <DurationIcon
                    duration={task.duration}
                    original_duration={task.original_duration}
                    original_work_time={task.work_time}
                    calculated_work_time={task.calculated_work_time}
                  />

                  <Pressable
                    style={[sy['flex-row'], sy['gap-2']]}
                    onPress={onShowDurationDialog}>
                    {isFinite(task.duration) ? (
                      <DurationText
                        duration={task.duration}
                        original_duration={task.original_duration}
                        original_work_time={task.work_time}
                        calculated_work_time={task.calculated_work_time}
                      />
                    ) : (
                      <Text
                        style={[
                          sy.smallCondensedRegular,
                          sy['text-lightgray'],
                          sy.underline,
                        ]}>
                        Keuze maken
                      </Text>
                    )}
                  </Pressable>
                </>
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  </>
);
Work.propTypes = {
  index: PropTypes.any,
  task: PropTypes.any,
  sla_operation: PropTypes.any,
  is_fleet_car: PropTypes.any,
  is_retail_car: PropTypes.any,
  hasDiscount: PropTypes.any,
  hasMarkup: PropTypes.any,
  onPress: PropTypes.any,
  onShowDiscountDialog: PropTypes.any,
  onShowMarkupDialog: PropTypes.any,
  onShowDurationDialog: PropTypes.any,
};
const Activity = ActivityRow;
Activity.propTypes = {
  disabled: PropTypes.any,
  rob_code: PropTypes.any,
  description: PropTypes.any,
  onPress: PropTypes.any,
};
const Operation = ({
  rob_component,
  operation_code,
  reason_code,
  onShowActionsheet,
}) => {
  if (!rob_component) {
    return <></>;
  }
  const operation = rob_component.operations.find(
    (item) => item.operation.code === operation_code,
  );
  const reason = operation?.reasons.find(
    (item) => item.code === reason_code,
  )?.description;
  return (
    <GroupingRow
      style={[sy['gap-2']]}
      contentStyle={[sy['flex-1']]}
      title={<Text style={sy.mediumPlus}>Bewerking</Text>}>
      <Link
        textStyle={[sy.truncate, sy['text-right']]}
        onPress={onShowActionsheet}>
        {operation ? (
          <>
            {operation.operation.description}
            {reason ? <>&nbsp;({reason})</> : <></>}
          </>
        ) : (
          <>Keuze maken</>
        )}
      </Link>
    </GroupingRow>
  );
};
Operation.propTypes = {
  rob_component: PropTypes.any,
  operation_code: PropTypes.any,
  reason_code: PropTypes.any,
  onShowActionsheet: PropTypes.any,
};
const Location = ({
  rob_component,
  operation_code,
  location,
  onShowActionsheet,
}) => {
  const operation = rob_component?.operations.find(
    (item) => item.operation.code === operation_code,
  );
  if (!operation?.locations?.length) {
    return null;
  }
  return (
    <GroupingRow
      style={[sy['gap-2']]}
      contentStyle={[sy['flex-1']]}
      title={<Text style={sy.mediumPlus}>Locatie</Text>}>
      <Link
        textStyle={[sy.truncate, sy['text-right']]}
        onPress={onShowActionsheet}>
        {location
          ?.split('')
          .map((item) => LOCATION_TEXT[item])
          .filter(Boolean)
          .join(' • ') ?? <>Keuze maken</>}
      </Link>
    </GroupingRow>
  );
};
Location.propTypes = {
  rob_component: PropTypes.any,
  operation_code: PropTypes.any,
  location: PropTypes.any,
  onShowActionsheet: PropTypes.any,
};
const Part = ({
  request_id,
  lessor,
  task_parts,
  sla_parts,
  onEditArticle,
  hasDiscount,
  partsDiscounts,
  hasMarkup,
  partsMarkups,
  onChangeQuantity,
  onChangeDiscount,
  onChangeMarkup,
}) => {
  const parts = task_parts.reduce((acc, item) => {
    acc[item.attributes.itemMpId] = {
      itemMpId: item.attributes.itemMpId,
      ...pickBy(item),
    };
    return acc;
  }, {});
  return (
    <>
      {Object.values(parts).map((item, index) => {
        const gen_art_nrs = item.gen_art_nr
          ? [item.gen_art_nr]
          : item.gen_arts ?? [];
        const sla_part = sla_parts?.find((x) =>
          gen_art_nrs.includes(x.gen_art_nr),
        );
        const is_option_part = !!item.attributes?.option_id;
        const quantity = item.quantity ?? 0;
        const description = item.description;
        const price = (() => {
          if (sla_part && !is_option_part) {
            return (
              (isFinite(item.price) ? item.price : sla_part.price) * quantity
            );
          }
          if (isFinite(item.price) && isFinite(item.quantity)) {
            return item.price * item.quantity;
          }
          return null;
        })();
        const is_sla_task_part = item.reference?.startsWith('SLA_TASK|');
        let discount_percentage;
        if (is_sla_task_part) {
          discount_percentage = Format.round(
            (item.discount / (item.quantity ?? 1) / item.price) * 100,
            0,
          );
        }
        const renderArticleText = () => {
          if (sla_part) {
            return (
              <SlaArticleText
                lessor={lessor}
                description={sla_part.description}
                attributes={item.attributes}
                specifications={item.specifications}
                request_id={request_id}
                itemMpId={item.attributes?.itemMpId}
              />
            );
          }
          if (isFinite(price)) {
            return <ArticleText source={item.source} number={item.number} />;
          }
          return <>Keuze maken</>;
        };
        return (
          <React.Fragment key={item.itemMpId}>
            <View style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}>
              <Quantity
                style={sy['pr-0']}
                quantity={quantity}
                onChange={() =>
                  onChangeQuantity(
                    item,
                    item.itemMpId,
                    item.amounts ?? quantity,
                  )
                }
              />
              <View
                style={[
                  sy['flex-row'],
                  sy['flex-1'],
                  sy['justify-between'],
                  sy['gap-4'],
                ]}>
                <Pressable
                  style={[sy['flex-1']]}
                  onPress={() =>
                    onEditArticle({
                      itemMpId: item.itemMpId,
                      description,
                      gen_art_nrs,
                      quantity,
                      amounts: item.amounts ?? 1,
                      task_part: item,
                      sla_part: is_option_part
                        ? {
                            id: item.id,
                            description: item.description,
                            price: item.price,
                            lessor,
                          }
                        : sla_part,
                    })
                  }>
                  <Text>{description}</Text>
                  <Text style={[sy.small, sy['text-lightgray'], sy.underline]}>
                    {renderArticleText()}
                  </Text>
                </Pressable>
                <View style={[sy['self-start'], sy['items-end']]}>
                  <Pressable
                    disabled={!hasDiscount && !hasMarkup}
                    onPress={() => {
                      if (hasDiscount) {
                        onChangeDiscount(
                          item.itemMpId,
                          price,
                          partsDiscounts[item.itemMpId] ?? 0,
                        );
                      }
                      if (hasMarkup) {
                        onChangeMarkup(
                          item.itemMpId,
                          price,
                          partsMarkups[item.itemMpId] ?? 0,
                        );
                      }
                    }}>
                    <Text
                      style={[
                        sy.smallRegular,
                        (hasDiscount || hasMarkup) && sy.underline,
                      ]}>
                      {isFinite(price) ? (
                        Format.price(
                          Math.max(
                            price +
                              (partsMarkups[item.itemMpId] ?? 0) -
                              (partsDiscounts[item.itemMpId] ?? 0),
                            0,
                          ),
                        )
                      ) : (
                        <></>
                      )}
                    </Text>
                  </Pressable>
                  <View style={[sy['flex-row'], sy['gap-1']]}>
                    {partsDiscounts[item.itemMpId] > 0 &&
                      (is_sla_task_part && discount_percentage > 0 ? (
                        <Menu
                          anchor={
                            <DiscountIcon
                              fill="#299976"
                              width={18}
                              height={18}
                            />
                          }
                          style={{
                            marginTop: 0,
                          }}
                          contentStyle={{
                            backgroundColor: '#6D6D6D',
                          }}>
                          <View
                            style={[sy['px-2'], sy['flex-row'], sy['gap-2']]}>
                            <DiscountIcon
                              width={18}
                              height={18}
                              fill="#FFFFFF"
                            />
                            <Text
                              style={[
                                sy.smallMedium,
                                {
                                  color: '#FFFFFF',
                                  whiteSpace: 'nowrap',
                                },
                              ]}>
                              {lessor}&nbsp;{Format.number(discount_percentage)}
                              %&nbsp;korting
                            </Text>
                            <Text
                              style={[
                                sy.smallMedium,
                                sy['line-through'],
                                {
                                  color: '#FFFFFF',
                                  whiteSpace: 'nowrap',
                                },
                              ]}>
                              {Format.price(item.price)}
                            </Text>
                          </View>
                        </Menu>
                      ) : (
                        <DiscountIcon fill="#299976" width={18} height={18} />
                      ))}
                    {partsMarkups[item.itemMpId] > 0 && (
                      <SellIcon fill="#FF6C00" width={18} height={18} />
                    )}
                    {item.source === SOURCES.Custom && (
                      <ProductionQuantityLimitsIcon
                        fill="#FF6C00"
                        width={18}
                        height={18}
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>
            {index < Object.values(parts).length - 1 && (
              <Divider style={[sy['bg-gray95'], sy['ml-14']]} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};
Part.propTypes = {
  request_id: PropTypes.any,
  lessor: PropTypes.any,
  task_parts: PropTypes.any,
  sla_parts: PropTypes.any,
  onEditArticle: PropTypes.any,
  hasDiscount: PropTypes.any,
  partsDiscounts: PropTypes.any,
  hasMarkup: PropTypes.any,
  partsMarkups: PropTypes.any,
  onChangeQuantity: PropTypes.any,
  onChangeDiscount: PropTypes.any,
  onChangeMarkup: PropTypes.any,
};
const History = ({mileage, work_parts, task_parts, history}) => {
  const parts = [...new Set([...work_parts, ...task_parts])]
    .map((item_mp_id) => history?.[item_mp_id])
    .filter(Boolean)
    // Strip location information
    .flatMap((item) => Object.values(item));
  if (parts.length === 0) {
    return <Text>Geen historie bekend</Text>;
  }
  return parts.map((part, index) => (
    <React.Fragment key={part.id ?? part.itemMpId ?? index}>
      <View style={[sy['p-4'], sy['flex-row'], sy['gap-4']]}>
        <Quantity style={[sy['pr-0']]} quantity={part.quantity} />
        <View style={[sy['flex-1'], sy['flex-row'], sy['justify-between']]}>
          <View style={[sy['flex-1']]}>
            <Text style={[sy.truncate]}>{part.description}</Text>
            <Text style={[sy.small, sy['text-lightgray']]}>{part.number}</Text>
          </View>
          {<HistoryAge part={part} mileage={mileage} />}
        </View>
      </View>
      {index < parts.length - 1 && <Divider style={sy['bg-gray95']} />}
    </React.Fragment>
  ));
};
History.propTypes = {
  mileage: PropTypes.any,
  work_parts: PropTypes.any,
  task_parts: PropTypes.any,
  history: PropTypes.any,
};
export const calculateTaskAmount = (
  selected,
  hourly_rate,
  workDuration,
  workDiscount,
  workMarkup,
  partsDiscounts,
  partsMarkups,
) => {
  let task_amount;
  if (selected?.warranty === 'Warranty') {
    task_amount = 0;
  } else if (isRobLeniencyTask(selected?.warranty)) {
    task_amount = selected?.leniency_amount;
  } else {
    const parts_amount = Object.values(selected?.parts ?? {})
      .filter((item) => isFinite(item.price) && isFinite(item.quantity))
      .reduce(
        (acc, item) =>
          acc +
          Math.max(
            item.price * item.quantity +
              (partsMarkups[item.attributes.itemMpId] ?? 0) -
              (partsDiscounts[item.attributes.itemMpId] ?? 0),
            0,
          ),
        0,
      );
    const duration = Object.values(workDuration).reduce(
      (acc, item) => acc + (item ?? 0),
      0,
    );
    task_amount =
      duration * (hourly_rate ?? 0) +
      parts_amount +
      Object.values(workMarkup ?? {}).reduce(
        (acc, item) => acc + (item ?? 0),
        0,
      ) -
      Object.values(workDiscount ?? {}).reduce(
        (acc, item) => acc + (item ?? 0),
        0,
      );
  }
  return task_amount;
};
const TaskAmount = ({
  selected,
  hourly_rate,
  workDuration,
  workDiscount,
  workMarkup,
  partsDiscounts,
  partsMarkups,
  style,
}) => {
  const task_amount = calculateTaskAmount(
    selected,
    hourly_rate,
    workDuration,
    workDiscount,
    workMarkup,
    partsDiscounts,
    partsMarkups,
  );
  return <Text style={style}>{Format.price(task_amount)}</Text>;
};
TaskAmount.propTypes = {
  selected: PropTypes.any,
  hourly_rate: PropTypes.any,
  workDuration: PropTypes.any,
  workDiscount: PropTypes.any,
  workMarkup: PropTypes.any,
  partsDiscounts: PropTypes.any,
  partsMarkups: PropTypes.any,
  style: PropTypes.any,
};
const HEADER_HEIGHT_IN_PX = 72;
const FOOTER_HEIGHT_IN_PX = 52;
export const DamageAdjustmentMenu = ({
  hasDiscount,
  hasMarkup,
  taskOptions,
  toggleHasDiscount,
  toggleHasMarkup,
}) => (
  <MoreMenu
    icon={
      <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
        {hasDiscount && <DiscountIcon fill="#299976" />}
        {hasMarkup && <SellIcon fill="#FF6C00" />}
        <WarrantyIcon
          value={{
            is_damage: true,
          }}
        />
      </View>
    }
    items={[
      {
        title: 'Korting',
        disabled: taskOptions?.discount === false,
        onPress: () => {
          if (hasMarkup) {
            toggleHasMarkup();
          }
          toggleHasDiscount();
        },
        leadingIcon: hasDiscount ? CheckIcon : null,
      },
      {
        title: 'Toeslag',
        disabled: taskOptions?.markup === false,
        onPress: () => {
          if (hasDiscount) {
            toggleHasDiscount();
          }
          toggleHasMarkup();
        },
        leadingIcon: hasMarkup ? CheckIcon : null,
      },
    ]}
  />
);

const Task = ({
  dimensions,
  onDismiss,
  license,
  is_fleet_car,
  is_retail_car,
  vin_number,
  mileage,
  tecrmi_enabled,
  request_id,
  damage_id,
  dealer_id,
  company_id,
  lessor,
  main_task,
  tasks,
  parts,
  component,
  rob,
  is_damage_request,
  onChange,
  onDelete,
  onShowDenialReasons,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState(SCREENS.TASK);
  const [works, setWorks] = useState(null);
  const [workParts, setWorkParts] = useState(null);
  const [worksLoading, setWorksLoading] = useState(!!main_task.id);
  const [codes, setCodes] = useState([main_task.rob_code].filter(Boolean));
  const [selected, setSelected] = useState({
    operation: main_task.operation,
    reason: main_task.reason,
    location: main_task.location,
    rob_code: main_task.rob_code,
    warranty: main_task.warranty,
    leniency_amount: main_task.task_amount,
    is_damage: main_task.is_damage,
    works: tasks.reduce(
      (acc, item) => ({
        ...acc,
        [item.work_id ?? item.reference]: {
          id: item.id,
          calculated_work_time: item.calculated_work_time,
          work_id: item.work_id,
          reference: item.reference,
          description: item.description,
          work_time: item.original_work_time,
          work_text: item.work_text,
          work_item_mp_id: item.work_item_mp_id,
          work_number: item.number,
          source: item.source,
        },
      }),
      {},
    ),
    parts:
      parts
        ?.filter((item) => item.attributes?.itemMpId)
        .reduce((acc, item) => {
          acc[item.attributes.itemMpId] = item;
          return acc;
        }, {}) ?? {},
  });
  const [workDuration, setWorkDuration] = useState(
    tasks.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item.duration,
      }),
      {},
    ),
  );
  const [workOriginalDuration, setWorkOriginalDuration] = useState(
    tasks.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item.original_duration,
      }),
      {},
    ),
  );
  const [workDiscount, setWorkDiscount] = useState(
    tasks.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item.discount,
      }),
      {},
    ),
  );
  const [workMarkup, setWorkMarkup] = useState(
    tasks.reduce(
      (acc, item) => ({
        ...acc,
        [item.id]: item.markup,
      }),
      {},
    ),
  );
  const [partsDiscounts, setPartsDiscounts] = useState(
    parts
      ?.filter((item) => item.attributes?.itemMpId)
      .reduce((acc, item) => {
        acc[item.attributes.itemMpId] = item.discount;
        return acc;
      }, {}) ?? {},
  );
  const [partsMarkups, setPartsMarkups] = useState(
    parts
      ?.filter((item) => item.attributes?.itemMpId)
      .reduce((acc, item) => {
        acc[item.attributes.itemMpId] = item.markup;
        return acc;
      }, {}) ?? {},
  );
  const [history, setHistory] = useState(null);
  const [hasHistory, setHasHistory] = useState(null);
  const [hourlyRate, setHourlyRate] = useState(null);
  const [slaParts, setSlaParts] = useState(null);
  const [slaCodeParts, setSlaCodeParts] = useState(null);
  const [slaOperations, setSlaOperations] = useState(null);
  const [robComponents, setRobComponents] = useState(null);
  const [selectedWorks, setSelectedWorks] = useState(null);
  const [priceCheck, setPriceCheck] = useState(null);
  const [hasDiscount, setHasDiscount] = useState(
    tasks.some((t) => t.discount > 0) ||
      parts?.some((item) => item.discount > 0),
  );
  const [hasMarkup, setHasMarkup] = useState(
    tasks.some((t) => t.markup > 0) || parts?.some((item) => item.markup > 0),
  );
  const [componentsWithDamageAllowed, setComponentsWithDamageAllowed] =
    useState(true);
  const [taskOptions, setTaskOptions] = useState(null);
  const toggleHasDiscount = () => {
    setHasDiscount((prevState) => {
      const newState = !prevState;
      if (newState) {
        setWorkMarkup({});
        setPartsMarkups({});
      } else {
        setWorkDiscount({});
        setPartsDiscounts({});
      }
      return newState;
    });
  };
  const toggleHasMarkup = () => {
    setHasMarkup((prevState) => {
      const newState = !prevState;
      if (newState) {
        setWorkDiscount({});
        setPartsDiscounts({});
      } else {
        setWorkMarkup({});
        setPartsMarkups({});
      }
      return newState;
    });
  };
  const [showEditQuantity, setShowEditQuantity] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [showPartsHistory, setShowPartsHistory] = useState(false);
  const [showOperationActionsheet, setShowOperationActionsheet] =
    useState(false);
  const [showLocationActionsheet, setShowLocationActionsheet] = useState(false);
  const [showLeniencyActionsheet, setShowLeniencyActionsheet] = useState(false);
  const [showWarrantyInfoDialog, setShowWarrantyInfoDialog] = useState(false);
  const [showDamageInfoDialog, setShowDamageInfoDialog] = useState(false);
  const [showAddPartActionsheet, setShowAddPartActionsheet] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(null);
  const [showMarkupDialog, setShowMarkupDialog] = useState(null);
  const [showDamageNotAllowedDialog, setShowDamageNotAllowedDialog] =
    useState(false);
  const [showDurationDialog, setShowDurationDialog] = useState(null);
  const [searchWork, setSearchWork] = useState(false);
  const transform_work_parts = useCallback(
    (work_parts) => {
      const parts = Object.fromEntries(
        Object.entries(selected.parts).filter(
          ([k, v]) =>
            v.source === SOURCES.LKQ ||
            v.source === SOURCES.PartsPoint ||
            slaCodeParts[main_task.rob_code]?.includes(`${v.gen_art_nr}`),
        ),
      );
      for (const part of work_parts ?? []) {
        if (!parts[part.itemMpId]) {
          parts[part.itemMpId] = {
            ...part,
          };
        }
      }
      return parts;
    },
    [selected, slaCodeParts, main_task],
  );
  const onPrefixChangeWork = useCallback(
    async (value) => {
      const {result, success} = await serviceRequestApi.search_works(
        request_id,
        value,
        {
          include_parts: true,
        },
      );
      return {
        result,
        success,
      };
    },
    [request_id],
  );
  const onAddWorks = useCallback(
    (changes) => {
      setWorks([
        ...works,
        ...changes.map((item) => ({
          work_id: item.work_id,
          work_item_mp_id: item.work_item_mp_id,
          work_number: item.number,
          work_text: item.work_text,
          work_time: item.work_time,
          source: item.source,
        })),
      ]);
      setSelectedWorks([
        ...selectedWorks,
        ...changes.map((item) => item.work_id),
      ]);
      for (const work of changes ?? []) {
        if (work.parts) {
          setWorkParts({
            ...workParts,
            [work.work_id]: work.parts,
          });
        }
      }
      setSearchWork(false);
    },
    [works, workParts, selectedWorks],
  );
  useEffect(() => {
    const fetchTask = async () => {
      if (!main_task.id) {
        return;
      }
      const {result} = await serviceRequestApi.task(
        request_id,
        main_task.id,
        'task',
      );
      const {task} = result;
      if (task.options) {
        setTaskOptions(task.options);
        if (!task.options.discount) {
          setHasDiscount(false);
        }
        if (!task.options.markup) {
          setHasMarkup(false);
        }
      }
    };

    // The works come from TecRMI and ROB-Net and are only needed once the
    // dealer edits the works or the ROB code, so they do not hold up the sheet.
    const fetchWorks = async () => {
      if (!main_task.id) {
        return;
      }
      setWorksLoading(true);
      const {result} = await serviceRequestApi.task(
        request_id,
        main_task.id,
        'works',
      );
      const {works, parts: work_parts, codes} = result;
      setWorks(
        works.map((item) => ({
          work_id: item.work_id,
          work_text: item.text,
          work_time: item.work_time,
          work_item_mp_id: item.item_mp_id,
          work_number: item.work_number,
          source: item.source,
        })),
      );
      setWorkParts(work_parts);
      setCodes([...new Set([...codes, main_task.rob_code].filter(Boolean))]);
      setWorksLoading(false);
    };
    const fetchRobComponents = async () => {
      const {result: robComponentsData} = is_damage_request
        ? await robApi.damage_components(license, request_id)
        : await robApi.components(license, request_id);
      setRobComponents(robComponentsData);
    };
    const fetchRobContract = async () => {
      const {result: rob_contract} = await robApi.contract(license, request_id);
      setComponentsWithDamageAllowed(
        rob_contract?.supplierContract.repairAndMaintenance
          .componentsWithDamageAllowed,
      );
    };
    const fetchHistory = async () => {
      const response = await serviceRequestApi.car_history(request_id);
      const {history} = response.result;
      setHistory(history);
    };
    const fetchSla = async () => {
      const {result: sla_result} =
        (await serviceRequestApi.sla(request_id)) ?? {};
      setSlaParts(sla_result?.parts);
      setSlaCodeParts(
        sla_result?.codes
          .filter((item) => item.parts)
          ?.reduce(
            (acc, item) => ({
              ...acc,
              [item.rob_code]: item.parts,
            }),
            {},
          ),
      );
      setSlaOperations(
        sla_result?.codes
          .filter((item) => item.operations)
          ?.reduce(
            (acc, item) => ({
              ...acc,
              [item.rob_code]: item.operations,
            }),
            {},
          ),
      );
    };
    const fetch = async () => {
      // The car history only feeds the parts history, which renders once it
      // arrives, so it does not hold up the sheet, and neither do the works.
      // The other calls are independent of each other and run side by side.
      fetchHistory().catch(trace);
      fetchWorks().catch(trace);
      await Promise.all([
        fetchTask(),
        ...(!is_fleet_car && !is_retail_car
          ? [fetchRobComponents(), fetchRobContract()]
          : []),
        fetchSla(),
      ]);
      setLoading(false);
    };
    fetch().catch(trace);
  }, [
    request_id,
    license,
    main_task,
    rob,
    vin_number,
    lessor,
    is_damage_request,
    is_fleet_car,
    is_retail_car,
  ]);
  useEffect(() => {
    const fetch = async () => {
      const {result} = await serviceRequestApi.sla_hourly_rate(
        request_id,
        selected.rob_code,
      );
      setHourlyRate(result.hourly_rate);
    };
    fetch().catch(trace);
  }, [request_id, selected?.rob_code]);
  useEffect(() => {
    setPriceCheck(null);
    const fetch = async () => {
      const {result: price_check} = await robApi.task_price_check(
        company_id,
        lessor,
        selected.rob_code,
        selected.operation,
        vin_number,
        hourlyRate,
      );
      setPriceCheck(price_check);
    };
    if (
      !rob &&
      Object.values(selected.works).length === 1 &&
      selected?.rob_code &&
      hourlyRate
    ) {
      fetch().catch(trace);
    }
  }, [
    selected?.works,
    selected?.rob_code,
    selected?.operation,
    hourlyRate,
    vin_number,
    lessor,
    company_id,
    rob,
  ]);
  useEffect(() => {
    if (!selected || !history) {
      return;
    }
    const itemMpIds = new Set(
      Object.values(selected.parts).map((item) => item.attributes.itemMpId),
    );
    setHasHistory(
      Object.keys(history).some((itemMpId) => itemMpIds.has(Number(itemMpId))),
    );
  }, [selected, history]);
  useEffect(() => {
    setSelected((prevState) => applySlaPartOptions(prevState, slaParts));
  }, [selected, slaParts]);
  const onChangeOperation = (operation_code, reason_code) => {
    const sla_operation = slaOperations?.[selected?.rob_code]?.find(
      (x) => x.operation === operation_code,
    );
    if (sla_operation) {
      setSelected({
        ...selected,
        operation: operation_code,
        reason: reason_code,
        works: {
          [null]: {
            ...main_task,
            reference: null,
            source: 'Invoer',
            work_id: null,
            work_text: null,
            work_time: null,
            original_work_time: null,
          },
        },
      });
      setWorkDuration({
        [main_task.id]: sla_operation.amount / hourlyRate,
      });
      setWorkOriginalDuration({
        [main_task.id]: null,
      });
      setWorkDiscount({});
      setWorkMarkup({});
      return;
    }
    if (selected.operation && selected.operation !== operation_code) {
      setSelected({
        ...selected,
        operation: operation_code,
        reason: reason_code,
        works: {
          [null]: {
            ...main_task,
            reference: null,
            source: 'Invoer',
            work_id: null,
            work_text: null,
            work_time: null,
            original_work_time: null,
          },
        },
      });
      setWorkDuration({
        [main_task.id]: null,
      });
      setWorkOriginalDuration({
        [main_task.id]: null,
      });
      setWorkDiscount({});
      setWorkMarkup({});
    } else {
      setSelected({
        ...selected,
        operation: operation_code,
        reason: reason_code,
      });
    }
  };
  const onAddParts = (parts) => {
    const changes = parts?.reduce((acc, {id, ...item}) => {
      acc[id] = {
        ...item,
      };
      return acc;
    }, {});
    setSelected({
      ...selected,
      parts: {
        ...selected.parts,
        ...changes,
      },
    });
    setShowAddPartActionsheet(false);
  };
  const retrieveChanges = () => {
    let main_work_id = Object.values(selected.works).find(
      (item) => item.id === main_task.id,
    )?.work_id;
    if (!main_work_id) {
      main_work_id = Object.keys(selected.works)[0];
    }
    const changed_tasks = Object.keys(selected.works)
      .sort((a, b) => a - b)
      .map((work_id) => {
        const work = works.find((item) => `${item.work_id}` === `${work_id}`);
        if (work) {
          const child = selected.works[work_id];
          return {
            ...child,
            id: main_work_id === work_id ? main_task.id : child.id,
            duration: workDuration[child.id] ?? 0,
            original_duration: workOriginalDuration[child.id] ?? null,
            discount: workDiscount[child.id] ?? 0,
            markup: workMarkup[child.id] ?? 0,
            work_id,
            reference: work_id,
            description: work.work_text,
            work_time: work.work_time,
            work_text: work.work_text,
            work_item_mp_id: work.work_item_mp_id,
            number: work.work_number,
            source: work.source,
          };
        } else {
          let task = {
            ...main_task,
            duration: workDuration[main_task?.id] ?? 0,
            original_duration: workOriginalDuration[main_task?.id] ?? null,
            discount: workDiscount[main_task?.id] ?? 0,
            markup: workMarkup[main_task?.id] ?? 0,
          };
          if (work_id === `${null}`) {
            return {
              ...task,
              work_id: null,
              reference: null,
              description: component.component_name,
              work_time: null,
              original_work_time: null,
              work_text: null,
              work_item_mp_id: null,
              number: null,
              source: 'Invoer',
            };
          }
          return task;
        }
      })
      .filter(Boolean);
    return {
      main_task_id: main_task.id,
      component_id: component.id,
      rob_code: selected.rob_code,
      operation: selected.operation,
      reason: selected.reason,
      location: selected.location,
      warranty: selected.warranty,
      leniency_amount: isRobLeniencyTask(selected.warranty)
        ? selected.leniency_amount
        : null,
      is_damage: selected.is_damage,
      tasks: changed_tasks,
      parts: Object.values(selected.parts).map((part) => ({
        ...part,
        discount: Math.min(
          partsDiscounts[part.attributes.itemMpId] ?? 0,
          part.price * part.quantity,
        ),
        markup: partsMarkups[part.attributes.itemMpId] ?? 0,
      })),
    };
  };
  const onOK = () => {
    const changes = retrieveChanges();
    onChange({
      ...changes,
      task_id: main_task.id,
    });
  };
  const onResetParts = () => {
    let parts = {};
    for (const work_id of Object.keys(selected?.works)) {
      parts = {
        ...parts,
        ...transform_work_parts(workParts?.[work_id]),
      };
    }
    setSelected({
      ...selected,
      parts: {
        ...parts,
      },
    });
  };
  if (showOperationActionsheet) {
    return (
      <OperationActionsheet
        onDismiss={() => setShowOperationActionsheet(false)}
        license={license}
        request_id={request_id}
        rob_code={selected.rob_code}
        operation_code={selected.operation}
        reason_code={selected.reason}
        is_damage_request={is_damage_request}
        onChange={({operation, reason}) => {
          onChangeOperation(operation, reason);
          setShowOperationActionsheet(false);
        }}
      />
    );
  }
  if (showLocationActionsheet) {
    return (
      <LocationActionsheet
        onDismiss={() => setShowLocationActionsheet(false)}
        license={license}
        request_id={request_id}
        rob_code={selected.rob_code}
        operation_code={selected.operation}
        location={selected.location}
        is_damage_request={is_damage_request}
        onChange={({location}) => {
          setSelected({
            ...selected,
            location,
          });
          setShowLocationActionsheet(false);
        }}
      />
    );
  }
  if (showAddPartActionsheet) {
    return (
      <PartSelection
        request_id={request_id}
        dealer_id={dealer_id}
        license={license}
        parts={selected.parts}
        lessor={lessor}
        rob_code={selected.rob_code}
        onChange={onAddParts}
        onDismiss={() => setShowAddPartActionsheet(false)}
      />
    );
  }
  if (editArticle) {
    return (
      <AddArticleActionsheet
        dismissable={false}
        onDismiss={() => setEditArticle(null)}
        request_id={request_id}
        license={license}
        dealer_id={dealer_id}
        description={editArticle.description}
        gen_arts={editArticle.gen_art_nrs}
        item_mp_id={editArticle.itemMpId}
        number={editArticle.task_part?.number}
        reference={editArticle.task_part?.reference}
        sla_part={editArticle.sla_part}
        task_part={editArticle.task_part}
        lessor={lessor}
        rob_code={selected.rob_code}
        onChange={({
          id,
          reference,
          number,
          source,
          description,
          brand,
          price,
          margin,
          quantity,
          gen_art_nr,
          specifications,
          lead_surcharge,
        }) => {
          if (lead_surcharge > 0) {
            selected.parts['LEAD_SURCHARGE'] = {
              reference: 'LEAD_SURCHARGE',
              source,
              description: 'Loodtoeslag',
              price: lead_surcharge,
              quantity: 1,
              attributes: {
                itemMpId: 'LEAD_SURCHARGE',
              },
            };
          }
          if (selected.parts[editArticle.itemMpId]) {
            selected.parts[editArticle.itemMpId] = updateExistingPart(
              selected.parts[editArticle.itemMpId],
              editArticle,
              {
                id,
                reference,
                number,
                source,
                description,
                brand,
                price,
                margin,
                quantity,
                gen_art_nr,
                specifications,
              },
            );
            setSelected({
              ...selected,
              parts: {
                ...selected.parts,
              },
            });
          } else {
            setSelected({
              ...selected,
              parts: {
                ...selected.parts,
                [editArticle.itemMpId]: {
                  reference,
                  number,
                  source,
                  description,
                  price,
                  gen_art_nr,
                  quantity: Math.max(
                    editArticle.quantity,
                    editArticle.amounts,
                    quantity ?? 0,
                    1,
                  ),
                  attributes: {
                    itemMpId: editArticle.itemMpId,
                    matching_code: id,
                  },
                  specifications,
                },
              },
            });
          }
          setEditArticle(null);
        }}
        onRemove={() => {
          selected.parts[editArticle.itemMpId] = null;
          setSelected({
            ...selected,
            parts: {
              ...pickBy(selected.parts),
            },
          });
          setEditArticle(null);
        }}
      />
    );
  }
  if (showLeniencyActionsheet) {
    return (
      <LeniencyActionsheet
        onDismiss={() => setShowLeniencyActionsheet(false)}
        value={selected.warranty}
        task_amount={main_task.task_amount}
        onChange={(leniency, leniency_amount) => {
          setSelected({
            ...selected,
            warranty: leniency,
            leniency_amount,
            is_damage: false,
          });
          setShowLeniencyActionsheet(false);
        }}
      />
    );
  }
  if (screen === SCREENS.ROB_CODE) {
    return (
      <RobCodeActionsheet
        request_id={request_id}
        license={license}
        codes={codes}
        rob_code={selected?.rob_code}
        onDismiss={() => setScreen(SCREENS.TASK)}
        onChange={(rob_code) => {
          setSelected({
            ...selected,
            rob_code,
            operation: robComponents[rob_code]?.operations[0].operation.code,
            reason: robComponents[rob_code]?.operations[0].reasons[0].code,
          });
          setScreen(SCREENS.TASK);
        }}
      />
    );
  }
  const sla_operation = slaOperations?.[selected?.rob_code]?.find(
    (o) => o.operation === selected?.operation,
  );
  const transformWork = (work) => {
    let work_id = null;
    if (Format.is_finite_number(work.work_id)) {
      work_id = work.work_id;
    } else if (Format.is_finite_number(work.reference)) {
      work_id = Number(work.reference);
    }
    return {
      ...work,
      work_id,
      hourly_rate: hourlyRate,
      duration: workDuration?.[work.id],
      original_duration: workOriginalDuration?.[work.id],
      markup: workMarkup?.[work.id],
      discount: workDiscount?.[work.id],
    };
  };
  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        {loading && (
          <View style={[sy['p-4']]}>
            <ActivityIndicator size="large" color="#231fda" />
          </View>
        )}
        {!loading && (
          <>
            {screen === SCREENS.TASK && (
              <>
                <Title>
                  <View
                    style={[
                      sy['flex-row'],
                      sy['justify-between'],
                      sy['items-center'],
                      sy['flex-1'],
                      {
                        width: '100%',
                      },
                    ]}>
                    <Text
                      style={[
                        sy.largePlus,
                        sy.truncate,
                        {
                          lineHeight: 30,
                        },
                      ]}>
                      {selected.rob_code
                        ? robComponents?.[selected.rob_code]?.description ??
                          component?.name
                        : component?.name
                            .replace(main_task.rob_code, '')
                            .trim()}
                    </Text>
                    <View style={[sy['flex-row']]}>
                      {onDelete && (
                        <Pressable onPress={() => onDelete(main_task.id)}>
                          <DeleteIcon fill="#4a4a49" />
                        </Pressable>
                      )}
                      {!rob && !!priceCheck && (
                        <PriceCheck
                          rob_code={
                            is_fleet_car || is_retail_car
                              ? null
                              : selected.rob_code
                          }
                          amount={calculateTaskAmount(
                            selected,
                            hourlyRate,
                            workDuration,
                            workDiscount,
                            workMarkup,
                            partsDiscounts,
                            partsMarkups,
                          )}
                          range={priceCheck?.range}
                          duration={workDuration[main_task.id]}
                          suggested_duration={priceCheck?.suggested_duration}
                          onChangeDuration={(suggested) => {
                            setWorkDuration((prevState) => ({
                              ...prevState,
                              [main_task.id]: suggested,
                            }));
                            setWorkOriginalDuration((prevState) => ({
                              ...prevState,
                              [main_task.id]: suggested,
                            }));
                          }}
                        />
                      )}
                      {rob?.status.code && (
                        <Pressable
                          style={[sy['flex-row']]}
                          disabled={is_retail_car || is_fleet_car}
                          onPress={() => onShowDenialReasons()}>
                          <RobTaskIcon
                            statuses={[rob?.status.code]}
                            height="24"
                            width="24"
                          />
                        </Pressable>
                      )}
                    </View>
                  </View>
                </Title>
                <Content
                  style={{
                    paddingBottom: 0,
                    paddingHorizontal: 0,
                  }}>
                  <ScrollView
                    contentContainerStyle={{
                      maxHeight:
                        dimensions.window.height -
                        HEADER_HEIGHT_IN_PX -
                        FOOTER_HEIGHT_IN_PX,
                    }}>
                    <Grouping
                      header="Arbeid"
                      initial={{
                        expand: true,
                      }}>
                      <View style={[sy['p-4'], sy['gap-4']]}>
                        {Object.values(selected.works)
                          .sort((a, b) => {
                            if (a.id === main_task.id) {
                              return -1;
                            }
                            if (b.id === main_task.id) {
                              return 1;
                            }
                            return a.work_text?.localeCompare(b.work_text);
                          })
                          .map(transformWork)
                          .map((task, index) => (
                            <Work
                              key={task.work_id ?? task.id ?? index}
                              index={index}
                              task={task}
                              sla_operation={sla_operation}
                              is_fleet_car={is_fleet_car}
                              is_retail_car={is_retail_car}
                              hasDiscount={hasDiscount}
                              hasMarkup={hasMarkup}
                              onPress={() => {
                                const selectedWorks = Object.keys(
                                  selected.works,
                                )
                                  .map((w) =>
                                    Format.is_finite_number(w)
                                      ? Number.parseInt(w, 10)
                                      : null,
                                  )
                                  .filter(Boolean);
                                setSelectedWorks(
                                  selectedWorks?.length > 0
                                    ? selectedWorks
                                    : null,
                                );
                                setScreen(SCREENS.WORK);
                              }}
                              onShowDiscountDialog={() =>
                                setShowDiscountDialog({
                                  task_id: task.id,
                                  discount: task.discount,
                                  amount: task.duration * hourlyRate,
                                  onChange: updateWorkAdjustment.bind(
                                    null,
                                    setWorkDiscount,
                                    task.id,
                                  ),
                                })
                              }
                              onShowMarkupDialog={() =>
                                setShowMarkupDialog({
                                  markup: task.markup,
                                  amount: task.duration * hourlyRate,
                                  onChange: updateWorkAdjustment.bind(
                                    null,
                                    setWorkMarkup,
                                    task.id,
                                  ),
                                })
                              }
                              onShowDurationDialog={() =>
                                setShowDurationDialog({
                                  task_id: task.id,
                                  source: task.source,
                                  work_time: task.work_time,
                                  calculated_work_time:
                                    task.calculated_work_time,
                                })
                              }
                            />
                          ))}
                      </View>
                    </Grouping>
                    <Divider style={sy['bg-gray95']} />
                    <View
                      style={[
                        sy['p-4'],
                        sy['flex-row'],
                        sy['justify-between'],
                      ]}>
                      <Text style={sy.mediumPlus}>Artikel(en)</Text>
                      <Pressable
                        onPress={() => setShowPartsHistory(!showPartsHistory)}>
                        {hasHistory && (
                          <>
                            {showPartsHistory ? (
                              <HistoryToggleOffIcon />
                            ) : (
                              <HistoryIcon />
                            )}
                          </>
                        )}
                      </Pressable>
                    </View>
                    <Divider style={sy['bg-gray95']} />
                    <View>
                      {hasHistory && showPartsHistory ? (
                        <History
                          mileage={mileage}
                          work_parts={Object.keys(selected.works)
                            .flatMap((work_id) => workParts?.[work_id])
                            .filter(Boolean)
                            .map((part) => part.itemMpId)}
                          task_parts={Object.values(selected.parts).map(
                            (item) => item.attributes.itemMpId,
                          )}
                          history={history}
                        />
                      ) : (
                        <Part
                          request_id={request_id}
                          lessor={lessor}
                          task_parts={Object.values(selected.parts)}
                          sla_parts={slaParts}
                          onEditArticle={setEditArticle}
                          hasDiscount={hasDiscount}
                          partsDiscounts={partsDiscounts}
                          hasMarkup={hasMarkup}
                          partsMarkups={partsMarkups}
                          onChangeQuantity={(task_part, itemMpId, amounts) => {
                            setShowEditQuantity({
                              initialValue: task_part?.quantity ?? amounts,
                              onChange: (value) => {
                                const changes = selected.parts;
                                if (value > 0) {
                                  const existingPart = changes[itemMpId]
                                    ? changes[itemMpId]
                                    : {};
                                  const existingAttributes = changes[itemMpId]
                                    ?.attributes
                                    ? changes[itemMpId].attributes
                                    : {
                                        itemMpId,
                                      };
                                  changes[itemMpId] = {
                                    ...existingPart,
                                    quantity: value,
                                    attributes: {
                                      ...existingAttributes,
                                    },
                                  };
                                } else {
                                  delete changes[itemMpId];
                                }
                                setSelected({
                                  ...selected,
                                  parts: {
                                    ...changes,
                                  },
                                });
                              },
                            });
                          }}
                          onChangeDiscount={(itemMpId, amount, discount) => {
                            setShowDiscountDialog({
                              discount,
                              amount,
                              onChange: (value) => {
                                setPartsDiscounts((prevState) => ({
                                  ...prevState,
                                  [itemMpId]: value,
                                }));
                              },
                            });
                          }}
                          onChangeMarkup={(itemMpId, amount, markup) => {
                            setShowMarkupDialog({
                              markup,
                              amount,
                              onChange: (value) => {
                                setPartsMarkups((prevState) => ({
                                  ...prevState,
                                  [itemMpId]: value,
                                }));
                              },
                            });
                          }}
                        />
                      )}
                    </View>
                    <Divider style={[sy['bg-gray95'], sy['ml-14']]} />
                    <View style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}>
                      <Pressable
                        style={[
                          sy['flex-row'],
                          sy['flex-1'],
                          sy['gap-4'],
                          sy['items-center'],
                        ]}
                        onPress={() => setShowAddPartActionsheet(true)}>
                        <AddIcon fill="#231fda" />
                        <View style={[sy['flex-1']]}>
                          <Text>Artikel toevoegen</Text>
                        </View>
                      </Pressable>
                      {Object.keys(selected.works)
                        .flatMap((work_id) => workParts?.[work_id])
                        .filter(Boolean)
                        .some((part) => !selected.parts[part.itemMpId]) && (
                        <Pressable onPress={onResetParts}>
                          <RestartIcon width={24} height={24} />
                        </Pressable>
                      )}
                    </View>
                    {robComponents && (
                      <Grouping
                        header="ROB-Net"
                        initial={{
                          expand: true,
                        }}>
                        <Activity
                          disabled={rob?.id || worksLoading}
                          rob_code={selected.rob_code}
                          description={
                            robComponents[selected?.rob_code]?.description
                          }
                          onPress={() => {
                            setScreen(SCREENS.ROB_CODE);
                          }}
                        />

                        <Operation
                          rob_component={robComponents[selected.rob_code]}
                          operation_code={selected.operation}
                          reason_code={selected.reason}
                          onShowActionsheet={() =>
                            setShowOperationActionsheet(true)
                          }
                        />

                        <Location
                          rob_component={robComponents[selected.rob_code]}
                          operation_code={selected.operation}
                          location={selected.location}
                          onShowActionsheet={() =>
                            setShowLocationActionsheet(true)
                          }
                        />
                      </Grouping>
                    )}
                  </ScrollView>
                </Content>
                <Divider style={sy['bg-gray88']} />
                <Actions style={[sy['justify-between']]}>
                  <View style={[sy['flex-row'], sy['items-center']]}>
                    <TaskAmount
                      selected={selected}
                      hourly_rate={hourlyRate}
                      workDuration={workDuration}
                      workDiscount={workDiscount}
                      workMarkup={workMarkup}
                      partsDiscounts={partsDiscounts}
                      partsMarkups={partsMarkups}
                      style={[sy.mediumBold, sy['pl-2'], sy['pr-4']]}
                    />
                    {is_damage_request ? (
                      <DamageAdjustmentMenu
                        hasDiscount={hasDiscount}
                        hasMarkup={hasMarkup}
                        taskOptions={taskOptions}
                        toggleHasDiscount={toggleHasDiscount}
                        toggleHasMarkup={toggleHasMarkup}
                      />
                    ) : (
                      <ActivityOptions
                        selected={selected}
                        setSelected={setSelected}
                        hasDiscount={hasDiscount}
                        hasMarkup={hasMarkup}
                        toggleHasDiscount={toggleHasDiscount}
                        toggleHasMarkup={toggleHasMarkup}
                        taskOptions={taskOptions}
                        componentsWithDamageAllowed={
                          componentsWithDamageAllowed
                        }
                        setShowWarrantyInfoDialog={setShowWarrantyInfoDialog}
                        setShowDamageInfoDialog={setShowDamageInfoDialog}
                        setShowLeniencyActionsheet={setShowLeniencyActionsheet}
                        setShowDamageNotAllowedDialog={
                          setShowDamageNotAllowedDialog
                        }
                      />
                    )}
                  </View>
                  <View style={[sy['flex-row'], sy['gap-2']]}>
                    <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
                    <ActionButton disabled={worksLoading} onPress={onOK}>
                      OK
                    </ActionButton>
                  </View>
                </Actions>
              </>
            )}
            {screen === SCREENS.WORK && worksLoading && (
              <View style={[sy['p-4']]}>
                <ActivityIndicator size="large" color="#231fda" />
              </View>
            )}
            {screen === SCREENS.WORK && !worksLoading && (
              <>
                <Title>
                  {is_fleet_car || is_retail_car
                    ? 'Activiteit'
                    : robComponents?.[selected.rob_code]?.operations.find(
                        (item) => item.operation.code === selected.operation,
                      )?.operation.description ?? main_task.description}
                </Title>
                <Divider style={sy['bg-gray88']} />
                <Content
                  style={{
                    paddingBottom: 0,
                    paddingHorizontal: 0,
                  }}>
                  <ScrollView
                    contentContainerStyle={{
                      maxHeight:
                        dimensions.window.height -
                        HEADER_HEIGHT_IN_PX -
                        FOOTER_HEIGHT_IN_PX,
                    }}>
                    <View style={[sy['flex-row'], sy['p-4']]}>
                      <View style={[sy['pr-8']]}>
                        <RadioButton
                          checked={!!selectedWorks}
                          onPress={() => setSelectedWorks([])}
                        />
                      </View>
                      <View
                        style={[
                          sy['flex-row'],
                          sy['flex-1'],
                          sy['justify-between'],
                        ]}>
                        <Text>TecRMI</Text>
                      </View>
                    </View>
                    <View>
                      {!!selectedWorks && (
                        <>
                          {works
                            .sort((a, b) => a.work_id - b.work_id)
                            .map((item) => (
                              <View
                                key={item.work_id}
                                style={[sy['flex-row'], sy['p-4'], sy['pl-8']]}>
                                <View style={[sy['pr-8']]}>
                                  <Checkbox
                                    disabledTimeout={0}
                                    checked={selectedWorks.includes(
                                      item.work_id,
                                    )}
                                    onPress={() => {
                                      if (
                                        selectedWorks?.includes(item.work_id)
                                      ) {
                                        setSelectedWorks(
                                          selectedWorks.filter(
                                            (w) => w !== item.work_id,
                                          ),
                                        );
                                      } else {
                                        setSelectedWorks([
                                          ...selectedWorks,
                                          item.work_id,
                                        ]);
                                      }
                                    }}
                                  />
                                </View>
                                <View
                                  style={[
                                    sy['flex-row'],
                                    sy['flex-1'],
                                    sy['justify-between'],
                                  ]}>
                                  <Text>{item.work_text}</Text>
                                  <Text>
                                    {Format.number(item.work_time, 2)}&nbsp;u
                                  </Text>
                                </View>
                              </View>
                            ))}
                          <Pressable
                            style={[sy['flex-row']]}
                            onPress={async () => {
                              if (!tecrmi_enabled) {
                                try {
                                  await new Promise((resolve, reject) => {
                                    dispatch(
                                      showTecrmiDialog({
                                        resolve,
                                        reject,
                                      }),
                                    );
                                  });
                                  await serviceRequestApi.enable_tecrmi(
                                    request_id,
                                  );
                                } catch {}
                              }
                              setSearchWork(true);
                            }}>
                            <View style={[sy['pl-8'], sy['p-4']]}>
                              <AddIcon fill="#231fda" />
                            </View>
                            <View style={[sy['flex-1']]}>
                              <View style={[sy['p-4']]}>
                                <Text>Taak toevoegen</Text>
                              </View>
                              <Divider style={sy['bg-gray95']} />
                            </View>
                          </Pressable>
                        </>
                      )}
                    </View>
                    <View>
                      <View style={[sy['flex-row'], sy['p-4']]}>
                        <View style={[sy['pr-8']]}>
                          <RadioButton
                            checked={!selectedWorks}
                            onPress={() => setSelectedWorks(null)}
                          />
                        </View>
                        <View
                          style={[
                            sy['flex-row'],
                            sy['flex-1'],
                            sy['justify-between'],
                          ]}>
                          <Text>Invoer</Text>
                        </View>
                      </View>
                    </View>
                  </ScrollView>
                </Content>
                <Divider style={sy['bg-gray88']} />
                <Actions>
                  <View style={[sy['flex-row'], sy['gap-2']]}>
                    <ActionButton onPress={() => setScreen(SCREENS.TASK)}>
                      Annuleren
                    </ActionButton>
                    <ActionButton
                      disabled={
                        !!selectedWorks &&
                        !works.some((item) =>
                          selectedWorks.includes(item.work_id),
                        )
                      }
                      onPress={() => {
                        let parts = {
                          ...selected.parts,
                        };
                        let changes = {};
                        if (selectedWorks) {
                          for (const work_id of selectedWorks) {
                            if (workParts?.[work_id]) {
                              parts = {
                                ...transform_work_parts(workParts?.[work_id]),
                                ...parts,
                              };
                            }
                            const task_id =
                              selected.works[work_id]?.id ?? uuid();
                            const work =
                              works.find((w) => w.work_id === work_id) ?? {};
                            setWorkDuration((prevState) => ({
                              ...prevState,
                              [task_id]: prevState[task_id] ?? work.work_time,
                            }));
                            setWorkOriginalDuration((prevState) => ({
                              ...prevState,
                              [task_id]: prevState[task_id] ?? null,
                            }));
                            changes = {
                              ...changes,
                              [work_id]: {
                                ...(selected.works[work_id]
                                  ? selected.works[work_id]
                                  : {}),
                                id: task_id,
                                reference: work.work_id,
                                source: 'TecRMI',
                                ...work,
                              },
                            };
                          }
                        } else {
                          changes = {
                            [null]: {
                              ...main_task,
                              reference: null,
                              number: null,
                              source: 'Invoer',
                              work_id: null,
                              work_text: null,
                              work_time: null,
                              original_work_time: null,
                            },
                          };
                          setWorkDuration({
                            [main_task.id]: null,
                          });
                          setWorkOriginalDuration({
                            [main_task.id]: null,
                          });
                          setWorkDiscount({});
                          setWorkMarkup({});
                        }
                        setSelected({
                          ...selected,
                          works: {
                            ...changes,
                          },
                          parts: {
                            ...parts,
                          },
                        });
                        setScreen(SCREENS.TASK);
                      }}>
                      OK
                    </ActionButton>
                  </View>
                </Actions>
              </>
            )}
          </>
        )}
      </Actionsheet>
      {showEditQuantity && (
        <QuantityDialog
          {...showEditQuantity}
          onDismiss={() => setShowEditQuantity(null)}
        />
      )}
      {showDurationDialog && (
        <DurationDialog
          priceCheck={priceCheck}
          work_source={showDurationDialog.source}
          sla={main_task?.sla}
          duration={workDuration[showDurationDialog.task_id]}
          original_duration={workOriginalDuration[showDurationDialog.task_id]}
          work_time={showDurationDialog.work_time}
          calculated_work_time={showDurationDialog.calculated_work_time}
          onChange={(duration, type) => {
            let change;
            switch (type) {
              case DURATION_HOUR_TYPES.suggested:
              case DURATION_HOUR_TYPES.work:
              case DURATION_HOUR_TYPES.custom: {
                change = duration;
                break;
              }
              default: {
                change = null;
                break;
              }
            }
            setWorkDuration((prevState) => ({
              ...prevState,
              [showDurationDialog.task_id]: change,
            }));
            setWorkOriginalDuration((prevState) => ({
              ...prevState,
              [showDurationDialog.task_id]:
                type === DURATION_HOUR_TYPES.work ? null : change,
            }));
          }}
          onDismiss={() => setShowDurationDialog(null)}
        />
      )}
      {searchWork && (
        <SearchWorkModal
          visible={true}
          works={works}
          onPrefixChange={onPrefixChangeWork}
          onAdd={onAddWorks}
          onDismiss={() => setSearchWork(false)}
        />
      )}
      {showWarrantyInfoDialog && (
        <WarrantyInfoDialog
          title={main_task.description}
          onDismiss={() => setShowWarrantyInfoDialog(false)}
        />
      )}
      {showDamageInfoDialog && (
        <Dialog
          visible={true}
          title="Schade"
          onDismiss={() => setShowDamageInfoDialog(false)}
          buttons={[
            {
              text: 'OK',
              onPress: () => setShowDamageInfoDialog(false),
            },
          ]}
          options={{
            noPaddingContent: true,
          }}>
          <View style={[sy['flex-row']]}>
            <View
              style={[
                sy['p-4'],
                {
                  flexShrink: 0,
                },
              ]}>
              <WarrantyIcon
                value={{
                  is_damage: true,
                }}
                width="24"
                height="24"
              />
            </View>
            <Text style={[sy['py-4']]}>
              Schadekenmerk toegepast voor activiteit.
            </Text>
          </View>
        </Dialog>
      )}
      <AdjustmentDialogs
        showDiscountDialog={showDiscountDialog}
        setShowDiscountDialog={setShowDiscountDialog}
        showMarkupDialog={showMarkupDialog}
        setShowMarkupDialog={setShowMarkupDialog}
        includeTaskId={true}
      />
      {showDamageNotAllowedDialog && (
        <DamageNotAllowedDialog
          license={license}
          rob={rob}
          request_id={request_id}
          damage_id={damage_id}
          component={component}
          onDismiss={({close_action_sheet}) => {
            setShowDamageNotAllowedDialog(false);
            if (close_action_sheet) {
              onDismiss();
            }
          }}
        />
      )}
    </>
  );
};
Task.propTypes = {
  dimensions: PropTypes.any,
  onDismiss: PropTypes.any,
  license: PropTypes.any,
  is_fleet_car: PropTypes.any,
  is_retail_car: PropTypes.any,
  vin_number: PropTypes.any,
  mileage: PropTypes.any,
  tecrmi_enabled: PropTypes.any,
  request_id: PropTypes.any,
  damage_id: PropTypes.any,
  dealer_id: PropTypes.any,
  company_id: PropTypes.any,
  lessor: PropTypes.any,
  main_task: PropTypes.any,
  tasks: PropTypes.any,
  parts: PropTypes.any,
  component: PropTypes.any,
  rob: PropTypes.any,
  is_damage_request: PropTypes.any,
  onChange: PropTypes.any,
  onDelete: PropTypes.any,
  onShowDenialReasons: PropTypes.any,
};
export default withDimensions(Task);
